import { useRef, forwardRef, useState, useEffect, useContext } from 'react';
import styles from './VideoComponent.module.css';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { byPrefixAndName } from '@awesome.me/kit-fe6b9b7683/icons';
import { GlobalContext } from '../contexts/globalContext';


const VideoFileComponent = forwardRef(({
    path, onShowControls, onHideControls, openCanvas, onPlay, onPause, onSeeked, fileUid, width, setWidth, height, setHeight, imageAspectRatioRef, setBaseSize, setTopicFilesOpened, file,
    heightRef, widthRef
}, ref) => {

    const { loadingFiles, setLoadingFiles, setAlertMessage } = useContext(GlobalContext)

    // const [videoDimensions, setVideoDimensions] = useState({ width: 0, height: 0 });
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [duration, setDuration] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [volume, setVolume] = useState(1);
    const [muted, setMuted] = useState(false);
    const [controlsVisible, setControlsVisible] = useState(false);
    const [fileName, setFileName] = useState("");
    const [isPortrait, setIsPortrait] = useState(false);

    const timeoutRef = useRef(null);
    const progressRef = useRef(null);
    const startingHeight = 1000;


    useEffect(() => {
        if (openCanvas) {
            if (!ref.current) return;

            ref.current.pause();

            setIsPlaying(false);
            showControls();
        }
    }, [openCanvas])

    const togglePlay = () => {
        if (!ref.current) return;

        if (isPlaying) {
            ref.current.pause();
        } else {
            ref.current.play();
        }

        setIsPlaying(prev => !prev);
        showControls();
    };

    const handleVolumeChange = (e) => {
        if (!ref.current) return;

        const newVolume = e.target.value;
        setVolume(newVolume);
        ref.current.volume = newVolume;
    };

    const toggleMute = () => {
        if (!ref.current) return;

        setMuted(prev => !prev);

        if (!muted) {
            ref.current.volumeValue = volume;
            setVolume(0);
        } else {
            setVolume(ref.current.volumeValue || 1);
        }
        ref.current.muted = !muted;

        showControls();
    };

    const showControls = () => {
        setControlsVisible(true);
        onShowControls?.();
        clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
            setControlsVisible(false);
            onHideControls?.();
        }, 5000);
    };

    const handleTimeUpdate = () => {
        if (!ref.current) return;
        const current = ref.current.currentTime;
        const total = ref.current.duration;
        setCurrentTime(current);
        setDuration(total);
        setProgress((current / total) * 100);

    };

    const handleProgressChange = (e) => {
        if (!ref.current) return;
        const newTime = (e.target.value / 100) * duration;
        ref.current.currentTime = newTime;
        setProgress(e.target.value);
        showControls();
        onSeeked?.();
    };

    const calculateWidth = (videoWidth, videoHeight) => {
        if (!ref.current) return;

        const containerHeight = heightRef ? heightRef.current : startingHeight;
        const videoAspectRatio = videoHeight / videoWidth;

        imageAspectRatioRef.current = videoAspectRatio;
        setWidth(containerHeight / videoAspectRatio);
        setHeight(containerHeight);
        setBaseSize({ w: containerHeight / videoAspectRatio, h: containerHeight });

        if(widthRef){
            widthRef.current = containerHeight / videoAspectRatio;
        }

        console.log("Calculated dimensions:", containerHeight / videoAspectRatio, containerHeight);
    };

    const handleClick = () => {

        if (controlsVisible) {
            setControlsVisible(false);
            onHideControls?.();
        } else {

            if (!openCanvas) {
                showControls();
            }

        }
    };

    useEffect(() => {
        if (!ref.current) return;

        ref.current.addEventListener("timeupdate", handleTimeUpdate);

        return () => {
            ref.current?.removeEventListener("timeupdate", handleTimeUpdate);
        };
    }, []);

    useEffect(() => {
        if (!ref.current) return;

        const loadMetaDataFunction = () => {
            const videoName = ref.current?.src.split('/').pop() || "Unknown Video";

            let processedName = videoName.length > 13 ? videoName.substring(13) : videoName;
            processedName = processedName.replace(/%20/g, " ");

            setFileName(processedName);
            if (ref.current) {
                // setVideoDimensions({
                //     width: ref.current.videoWidth,
                //     height: ref.current.videoHeight
                // });
                setIsPortrait(ref.current.videoHeight > ref.current.videoWidth);
            }

            calculateWidth(ref.current?.videoWidth, ref.current?.videoHeight);

            var filtrat = loadingFiles.filter(a => a !== fileUid);
            setLoadingFiles(filtrat)
        }

        ref.current.addEventListener("loadedmetadata", loadMetaDataFunction);

        return () => {
            if (ref.current) {
                ref.current.removeEventListener("scroll", loadMetaDataFunction);
            }
        };
    }, []);


    return (
        <div style={{ position: "relative", width: width, height: height }} onClick={handleClick}>
            <video ref={ref} src={path} className={styles.video} onPlay={onPlay} onPause={onPause} loop onError={() => {
                    setAlertMessage({ type: "ERROR", message: "The file is no longer at the address provided." })
                    if(file){
                        setTopicFilesOpened(files => files.filter(a => a.uid !== file.uid));
                    }
                }}/>

            <div className={styles.footer} style={{ opacity: controlsVisible ? 1 : 0, bottom: 0 }}>
                <label className={styles.labelNameFile}>{fileName || "Unknown Video"}</label>
                <div className={`${styles.controlsContainer} ${isPortrait ? styles.vertical : ''}`} style={{ pointerEvents: controlsVisible ? 'auto' : 'none' }}>
                    <div onClick={togglePlay} style={{ height: 29 }}>
                        {isPlaying ? (
                            <FontAwesomeIcon className={styles.iconControl} icon={byPrefixAndName.fas['pause']} />
                        ) : (
                            <FontAwesomeIcon className={styles.iconControl} icon={byPrefixAndName.fas['play']} />
                        )}
                    </div>
                    <span className={styles.time}>
                        {Math.floor(currentTime / 60)}:{("0" + Math.floor(currentTime % 60)).slice(-2)}
                    </span>
                    <input
                        type="range"
                        ref={progressRef}
                        className={styles.progressBar}
                        min="0"
                        max="100"
                        step="1"
                        value={progress}
                        onChange={handleProgressChange}
                        style={{
                            background: `linear-gradient(to right, #ffffff ${progress}%, #5a5454 ${progress}%)`,
                        }}
                    />
                    <span className={styles.time} style={{ marginRight: 16 }}>
                        {Math.floor(duration / 60)}:{("0" + Math.floor(duration % 60)).slice(-2)}
                    </span>


                    <div className={styles.volumeIcon} onClick={toggleMute}>
                        {muted ? (
                            <FontAwesomeIcon className={styles.volumeIcon} icon={byPrefixAndName.fas['volume-slash']} />
                        ) : (
                            <FontAwesomeIcon className={styles.volumeIcon} icon={byPrefixAndName.fas['volume']} />
                        )}
                    </div>
                    <input
                        type="range"
                        className={styles.volumeBar}
                        min="0"
                        max="1"
                        step="0.01"
                        value={muted ? 0 : volume}
                        onChange={handleVolumeChange}
                        disabled={muted}
                        style={{
                            background: `linear-gradient(to right, #ffffff ${volume * 100}%, #5a5454 ${volume * 100}%)`,
                            pointerEvents: controlsVisible ? 'auto' : 'none'
                        }}
                    />
                </div>
            </div>


        </div>


    );
});

export default VideoFileComponent;
